import { cloneDeep } from 'lodash'
import { uuid4 } from '@sentry/utils'
import {
  COMPONENT,
  GROUP,
  LABEL,
  SHAPE,
  INPUT,
  LIST,
  SECTION,
  ELLIPSE,
  IMAGE,
  IMAGE_UPLOAD,
  FILE_UPLOAD,
  DATE_PICKER,
  LIBRARY_COMPONENT,
  SELECT,
  LOCATION_INPUT,
  dataTypes,
  responsivePositioningOptions,
  resizingOptions,
  DeviceType,
  DeviceWidth,
  DeviceBreakpoint,
  LAYOUT_SECTION,
  FORM,
} from '@adalo/constants'

import {
  insert,
  update,
  remove,
  evaluate,
  getBoundingBox,
  getDropPath,
  getGroupPath,
  getInsertPath,
  getObject,
  incrementPath,
  decrementPath,
  isChildPath,
  joinPaths,
  remapSiblings,
  removeChildren,
  subPath,
  pathLength,
  nextPath,
  translateChildren,
  mergeReducers,
  getId,
  uniqueElements,
  sortPaths,
  deepMerge,
  getParentPath,
  getDeviceType,
  getSiblings,
} from '@adalo/utils'

import { createDefaultLayoutSection } from 'utils/layoutSections/modifiers'
import getLayoutSectionFitForScreen from 'utils/layoutSections/modifiers/getLayoutSectionFitForScreen'
import createLayoutSectionFromPrebuilt from 'utils/layoutSections/modifiers/createLayoutSectionFromPrebuilt'
import { setGlobalDefaults } from 'utils/library-globals'
import {
  getSectionFromChild,
  getSectionFromSectionElement,
  isContainerSectionElement,
  isSectionElement,
  shouldReparentComponent,
  getLayoutHelperFromSection,
  getContainerFromLayoutHelper,
  getContainerChildrenFromSection,
} from 'utils/layoutSections'
import { convertType, indexByType } from 'utils/objects'
import createEmptyObject from 'ducks/editor/objects/createEmptyObject'

import { getDefaultListResponsivity } from 'utils/defaultConstraints'

import {
  updateComponentBounds,
  getAppComponent,
  hasRole,
} from 'utils/libraries'
import { saveTouched, saveDiffed, setPrevList } from 'utils/saving'
import { requestApp, saveLibraryGlobals } from 'utils/io'
import { updateObjectWidth } from 'utils/text'
import { getSnapGrid } from 'utils/snapping'
import { calculateZoom, getOffset } from 'utils/zoom'
import { updateOptions, updateParentOptions } from 'utils/groupTypes'
import { convertToInput } from 'utils/conversion'
import updateParentBounds from 'utils/operations/updateParentBounds'
import updateBounds from 'utils/operations/updateBounds'
import resizeParent from 'utils/operations/shouldResizeParent'

import {
  getBestParent,
  getInsertPosition,
  getAbsoluteBbox,
  alignToRect,
} from 'utils/geometry'
import { defaultListChildren, responsiveDefaultListChildren } from 'utils/lists'
import { isSupportedOnWeb } from 'utils/platform'

import { getAccordionState, LIBRARY_INSPECT_GROUP } from 'ducks/accordions'

import {
  changeObjectParent,
  getObjectAlignment,
  getParentScreen,
} from 'utils/positioning'
import { getFeatureFlag } from 'ducks/featureFlags'
import getDeviceObject from 'utils/getDeviceObject'
import inferFixedPositionAnchor from 'utils/objects/inferFixedPositionAnchor'
import { getWidthConstraints } from 'utils/objects/widthContraints'
import { evaluateUserflowEvents, USERFLOW_EVENTS } from 'utils/userflow'
import { getApp } from 'ducks/apps'
import selectionReducer, {
  SELECT_PARENT,
  getActiveComponent,
} from './selection'

import { SET_TOOL } from './tools'

import textEditingReducer from './textEditing'
import shapeEditingReducer from './shapeEditing'
import sketchUploadReducer from './sketchUpload'
import positioningReducer from './positioning'
import clipboardReducer from './clipboard'
import snappingReducer, { snappingMiddleware } from './snapping'

import {
  applyInstructions,
  disableDeviceSpecificLayout,
  enableDeviceSpecificLayout,
  getTouchedFromInstruction,
  moveElement,
  moveScreen,
  resizeElement,
  resizeScreen,
  updateElement,
} from './instructions'
import {
  applyLayoutInstruction,
  getTouchedFromInstruction as getSelectionTouchedFromInstruction,
} from './selection-instructions'

import calculatePushGraphs from './pushing/calculatePushGraphs'
import getContainingScreen from './objects/getContainingScreen'
import coerceScreenResizeToMinimums from './positioning/coerceScreenResizeToMinimums'
import { getParent } from './device-layouts/utils'

export const REQUEST_DATA = Symbol('REQUEST_DATA')
export const SET_DATA = Symbol('SET_DATA')
export const CREATE_OBJECT = Symbol('CREATE_OBJECT')
export const UPDATE_OBJECT = Symbol('UPDATE_OBJECT')
export const UPDATE_OBJECTS = Symbol('UPDATE_OBJECTS')
export const UPDATE_OBJECT_BOUNDS = Symbol('UPDATE_OBJECT_BOUNDS')
export const CHANGE_OBJECT_TYPE = Symbol('CHANGE_OBJECT_TYPE')
export const RESIZE_OBJECT = Symbol('RESIZE_OBJECT')
export const POSITION_OBJECTS = Symbol('POSITION_OBJECTS')
export const DELETE_OBJECT = Symbol('DELETE_OBJECT')
export const REORDER_OBJECTS = Symbol('REORDER_OBJECTS')
export const REORDER_OBJECTS_MOVE_LAST = Symbol('REORDER_OBJECTS_MOVE_LAST')
export const REORDER_OBJECTS_MOVE_FIRST = Symbol('REORDER_OBJECTS_MOVE_FIRST')
export const REORDER_OBJECTS_MOVE_UP = Symbol('REORDER_OBJECTS_MOVE_UP')
export const REORDER_OBJECTS_MOVE_DOWN = Symbol('REORDER_OBJECTS_MOVE_DOWN')
export const GROUP_OBJECTS = Symbol('GROUP_OBJECTS')
export const GROUP_OBJECTS_TO_LIST = Symbol('GROUP_OBJECTS_TO_LIST')
export const UNGROUP_OBJECTS = Symbol('UNGROUP_OBJECTS')
export const SET_PAGE_SIZE = Symbol('SET_PAGE_SIZE')
export const ALIGN_OBJECTS = Symbol('ALIGN_OBJECTS')
const ZOOM = Symbol('ZOOM')
const RESET_ZOOM = Symbol('RESET_ZOOM')
const PAN = Symbol('PAN')
const SET_LIBRARY_GLOBAL = Symbol('SET_LIBRARY_GLOBAL')
export const RUN_INSTRUCTIONS = Symbol('RUN_INSTRUCTIONS')
export const RUN_SELECTION_INSTRUCTION = Symbol('RUN_SELECTION_INSTRUCTION')

const { LEFT, RIGHT, CENTER, LEFT_AND_RIGHT } = responsivePositioningOptions
const { SCALES_WITH_PARENT } = resizingOptions

const DISABLED_ACTIONS_FOR_SECTION_ELEMENTS = new Set([
  REORDER_OBJECTS_MOVE_FIRST,
  REORDER_OBJECTS_MOVE_LAST,
  REORDER_OBJECTS_MOVE_UP,
  REORDER_OBJECTS_MOVE_DOWN,
])

export const INITIAL_STATE = {
  appId: null,
  loading: false,
  name: null,
  zoom: {
    scale: 1,
    offset: [0, 0],
  },
  map: {},
  parentMap: {},
  list: [],

  // Indices map to components at the top level
  // typeIndex: { COMPONENT1: { TYPE: [...] } }
  // Others: { COMPONENT1: [...] }
  typeIndex: {},
  selection: [],
  hoverSelection: [],
  activeComponent: null,
  textEditing: null,
  shapeEditing: null,
  draggingOutControl: false,
  draggingInControl: false,
  selectedPoint: null,
  draggingSelectedPoint: false,
  zoomAppId: null,
  dragging: false,
  panning: false,
  xGrid: null,
  yGrid: null,
  currentXSnap: null,
  currentYSnap: null,
  positioningObjects: null,
  positioningStartPoint: null,
  positioningConstraint: null,
  sketchUpload: {
    uploadInProgress: false,
    uploadError: null,
    progress: null,
  },
  selectedParent: null,
  libraryGlobals: {},
}

const getComponentObject = (components, id) => {
  const componentObj = components[id]

  const {
    x,
    y,
    width,
    height,
    objects,
    pushGraph,
    mobile,
    tablet,
    desktop,
    name,
    backgroundColor,
    backgroundImage,
    backgroundPositionX,
    backgroundPositionY,
    backgroundSize,
    statusBarStyle,
    reverseScroll,
    reusableComponent,
    onVisit,
    componentActions,
    screenTemplate,
  } = componentObj

  return {
    id,
    name,
    backgroundColor,
    backgroundImage,
    backgroundPositionX,
    backgroundPositionY,
    backgroundSize,
    onVisit,
    componentActions,
    type: COMPONENT,
    x: x || 0,
    y: y || 0,
    width,
    height,
    reusableComponent,
    statusBarStyle,
    reverseScroll,
    screenTemplate,
    children: objects,
    pushGraph,
    mobile,
    tablet,
    desktop,
  }
}

export const getParentId = (
  list,
  map,
  typeIndex,
  parentTypes,
  newObject,
  mouseCoords,
  zoom
) => {
  const { magicLayout } = newObject
  const containerIds = []

  const potentialParentFilter = listId => {
    if (listId === newObject.id) {
      return false
    }

    if (
      map[newObject.id] &&
      isChildPath(map[newObject.id] || '', map[listId])
    ) {
      return false
    }

    return true
  }

  // This needs to iterate over all screens in case the object was dragged from one screen to another.
  // The performance is passable in most cases due to the typeindex.
  for (const component of list) {
    const screenElementsByType = typeIndex[component.id]
    if (!screenElementsByType) {
      continue
    }

    for (const parentType of parentTypes) {
      let listIds = screenElementsByType[parentType] || []

      if (magicLayout) {
        const device = getDeviceType(component.width)

        const removeHidden = id => {
          const containerObject = getObject(list, map[id])

          if (
            !containerObject ||
            (containerObject.deviceVisibility &&
              !containerObject.deviceVisibility[device])
          ) {
            return false
          }

          return isParentVisible(containerObject, device, list, map)
        }

        listIds = listIds.filter(removeHidden)
      }

      containerIds.push(...listIds.filter(potentialParentFilter))
    }
  }

  const canvasRelativeBBoxes = containerIds.map(listId => {
    let device

    if (magicLayout) {
      const screen = getParentScreen(list, map, listId)
      device = screen ? getDeviceType(screen.width) : undefined
    }

    return getAbsoluteBbox(getObject(list, map[listId]), list, map, device)
  })

  let currentParent = null

  if (map && map[newObject.id]) {
    currentParent = getParent(list, map, newObject.id)
  }

  return getBestParent(
    newObject,
    list.concat(...canvasRelativeBBoxes),
    map,
    currentParent,
    zoom,
    mouseCoords
  )
}

const updatePositioning = (list, pathMap, objectId, mouseCoords = {}) => {
  const obj = getObject(list, pathMap[objectId])

  const { y, height } = obj
  const { height: screenHeight } = getContainingScreen(list, pathMap, objectId)

  const positioning = inferFixedPositionAnchor(y, height, screenHeight)

  return update(list, pathMap[objectId], {
    ...obj,
    positioning,
  })
}

const innerPerformCreate = (
  object,
  list,
  objects,
  id,
  newIds,
  state,
  parentId,
  map,
  path,
  isCopy,
  isRelative,
  setSelection,
  zoom,
  selection,
  objectIds,
  newObjects,
  changeIds = true,
  includeInitialDevice = false,
  hasUpdatedGroups = false,
  mobileOnly = false,
  hasSectionComponent = false,
  mouseCoords = {},
  hasMinMaxWidth = false
) => {
  let newObject = createEmptyObject(
    list,
    map,
    object,
    changeIds,
    mobileOnly,
    hasMinMaxWidth
  )

  if (objects.length === 1 && id) {
    newObject.id = id
  }

  newIds.push(newObject.id)

  if (!parentId) {
    const parentTypes = [LIST]
    if (state && state.magicLayout) {
      parentTypes.push(SECTION, IMAGE, ELLIPSE)

      if (hasUpdatedGroups) {
        parentTypes.push(GROUP)
      }
    }

    parentId = getParentId(
      list,
      map,
      state.typeIndex,
      parentTypes,
      {
        ...newObject,
        magicLayout: state && state.magicLayout,
      },
      mouseCoords,
      zoom
    )

    if (hasSectionComponent) {
      const component = getObject(list, subPath(map[parentId], 1))
      const parentPath = map[parentId]
      const parent = parentPath ? getObject(list, parentPath) : undefined

      if (!shouldReparentComponent(object, parent)) {
        parentId = component.id
      }
    }
  }

  let newPath = getInsertPath(list, path, map[parentId])

  if (isCopy && path) {
    newPath = path
    path = nextPath(path)
  }

  if (newObject.type === COMPONENT && pathLength(newPath) > 1) {
    newPath = nextPath(subPath(newPath, 1))
  }

  if (parentId) {
    const component = getObject(list, subPath(map[parentId], 1))
    const device = getDeviceType(component.width)

    if (!isRelative) {
      newObject.x -= component.x
      newObject.y -= component.y
    }

    // Right now we should only set the initial device when the hasAutoCustomLayout flag is set
    // TODO(enzo): disabling initialDevice on test env so tests work as pre- auto custom layouts, until we update the tests and add tests for this feature

    if (includeInitialDevice && process.env.NODE_ENV !== 'test') {
      newObject.initialDevice = device
      if ((newObject.children?.length ?? 0) > 0) {
        newObject.children.forEach(child => {
          child.initialDevice = device
        })
      }
    }
  } else {
    // Get out of here if trying to insert non-component at top-level
    if (newObject.type !== COMPONENT) {
      return null
    }

    if (
      !isRelative &&
      !(newObject.x !== undefined || newObject.y !== undefined)
    ) {
      const position = getInsertPosition(list)
      newObject = { ...newObject, ...position }
    }

    if (setSelection) {
      zoom = calculateZoom(newObject)
    }
  }

  newObject = updateObjectWidth(newObject)

  const magicLayout = state && state.magicLayout

  list = insert(list, newPath, newObject)
  map = remapSiblings(list, map, newPath)
  list = updateParentBounds(
    list,
    map,
    newObject.id,
    null,
    resizeParent,
    null,
    magicLayout
  )
  list = updateOptions(list, map, newObject.id)
  if (state && state.magicLayout) {
    list = updatePositioning(list, map, newObject.id, mouseCoords)
  }

  if (newObject.type !== COMPONENT) {
    selection.push(newObject.id)
  }

  if (
    newObject.type === COMPONENT &&
    newObject.objects &&
    newObject.objects.length > 0
  ) {
    const children = newObject.objects
    newObject.objects = []

    children.forEach(child => {
      const result = innerPerformCreate(
        child,
        list,
        [],
        null,
        newIds,
        state,
        newObject.id,
        map,
        path,
        isCopy,
        true,
        setSelection,
        zoom,
        [],
        objectIds,
        newObjects,
        changeIds,
        includeInitialDevice,
        hasUpdatedGroups,
        mobileOnly,
        hasSectionComponent,
        mouseCoords,
        hasMinMaxWidth
      )

      ;({ list, map } = result)
    })
  }

  if (
    newObject.type === LIST &&
    !isCopy &&
    (!newObject.children || newObject.children.length === 0)
  ) {
    const defaultChildren = state.magicLayout
      ? responsiveDefaultListChildren
      : defaultListChildren

    const defaultChildResponsivity = getDefaultListResponsivity()
    if (mobileOnly) {
      defaultChildResponsivity.horizontalPositioning = CENTER
    }

    defaultChildren.forEach(child => {
      child = {
        ...child,
        x: newObject.x + child.x,
        y: newObject.y + child.y,
        id: getId(),
        responsivity: defaultChildResponsivity,
      }

      if (state.magicLayout && child.children) {
        child.children = child.children.map(item => ({
          ...item,
          x: item.x + child.x,
          y: item.y + child.y,
          id: getId(),
          responsivity: defaultChildResponsivity,
        }))
      }

      const result = innerPerformCreate(
        child,
        list,
        [],
        null,
        newIds,
        state,
        newObject.id,
        map,
        path,
        isCopy,
        true,
        setSelection,
        zoom,
        [],
        objectIds,
        newObjects,
        changeIds,
        includeInitialDevice,
        hasUpdatedGroups,
        mobileOnly,
        hasSectionComponent,
        mouseCoords,
        hasMinMaxWidth
      )

      ;({ list, map } = result)
    })
  }

  objectIds.push(newObject.id)
  newObjects.push(newObject)

  return { list, parentId, map, path, zoom }
}

export const performCreate = (
  state,
  path,
  objects,
  id,
  parentId,
  isRelative = false,
  isCopy = false,
  setSelection = true,
  changeIds = true,
  includeInitialDevice = false,
  hasUpdatedGroups = false,
  mobileOnly = false,
  hasSectionComponent = false,
  mouseCoords = {},
  hasMinMaxWidth = false
) => {
  let { list, map, zoom, magicLayout } = state
  const objectIds = []
  const selection = []
  const newObjects = []
  const newIds = []

  if (
    objects?.type === LAYOUT_SECTION &&
    (objects?.children?.length ?? 0) > 0
  ) {
    // TODO(enzo): this seems to be necessary with the way we're using the layout section input objects,
    // right now the layout section template objects are hardcoded. after we fetch them from backend we should change this.
    objects = cloneDeep(objects)
  }

  if (!Array.isArray(objects)) {
    objects = [objects]
  }

  if (!parentId && state.selectedParent) {
    parentId = state.selectedParent
  }

  state = {
    ...state,
    selectedParent: null,
  }

  const componentIds = []
  for (const object of objects) {
    const result = innerPerformCreate(
      object,
      list,
      objects,
      id,
      newIds,
      state,
      parentId,
      map,
      path,
      isCopy,
      isRelative,
      setSelection,
      zoom,
      selection,
      objectIds,
      newObjects,
      changeIds,
      includeInitialDevice,
      hasUpdatedGroups,
      mobileOnly,
      hasSectionComponent,
      mouseCoords,
      hasMinMaxWidth
    )

    if (result == null) {
      // Get out of here if trying to insert non-component at top-level
      return state
    }

    ;({ list, map, parentId, path, zoom } = result)

    const { type } = object

    if (type === COMPONENT) {
      if (isCopy) {
        const newScreenId =
          newIds.length > 0 ? newIds[newIds.length - 1] : object.id
        if (newScreenId) {
          componentIds.push(newScreenId)
        }
      } else {
        // screen templates
        componentIds.push(id)
      }
    }

    if (type !== COMPONENT && parentId !== null) {
      const { id: componentId } = getObject(list, subPath(map[parentId], 1))

      if (!componentIds.includes(componentId)) {
        componentIds.push(componentId)
      }
    }
  }

  if (magicLayout) {
    // Runs when adding a new component directly into a container type either by:
    // - dragging from the Add Component panel
    // - using hotkeys and clicking into the container type
    const newParentObjectIds = []

    if (isCopy === false && parentId && newObjects.length > 0) {
      for (const object of newObjects) {
        if (object.type === COMPONENT) {
          continue
        }

        const parent = getParent(list, map, object.id)

        if (parent.id !== parentId) {
          continue
        }

        newParentObjectIds.push(object.id)
      }
    }

    for (const componentId of componentIds) {
      const component = getObject(list, map[componentId])

      // TODO (michael-adalo): re-consider this logic when turning Create Object into an Instruction
      const device = getDeviceType(component.width)

      if (component.isTemplate && component.isTemplate === true) {
        delete component.objects
        list = update(list, map[componentId], component)
      }

      list = calculatePushGraphs(list, map, componentId)

      // we should not reset any device specific layout config
      // if the parent component is the actual screen
      const isParentScreen = parentId === component?.id

      for (const objectId of newParentObjectIds) {
        const { x, y, width, height } = getDeviceObject(
          getObject(list, map[objectId]),
          device
        )

        const instructions = []

        if (!isParentScreen) {
          instructions.push(
            ...[DeviceType.DESKTOP, DeviceType.TABLET, DeviceType.MOBILE].map(
              deviceType => disableDeviceSpecificLayout(objectId, deviceType)
            )
          )
        }

        instructions.push(moveElement(objectId, x, y))
        instructions.push(resizeElement(objectId, width, height))
        ;({ list } = applyInstructions({ list, pathMap: map }, instructions))
      }
    }
  }

  const libraryGlobals = setGlobalDefaults(state.libraryGlobals, newObjects)

  saveTouched(
    state.appId,
    list,
    map,
    newObjects.map(o => o.id),
    undefined,
    undefined,
    libraryGlobals
  )

  let newObject = newObjects.length === 1 ? newObjects[0] : {}

  if (newObject.type === LAYOUT_SECTION && hasSectionComponent) {
    const component = getContainingScreen(list, map, parentId)
    const screenWidth = component.width
    const screenHeight = component.height
    const currentDevice = getDeviceType(screenWidth)

    const allDevices = [
      DeviceType.DESKTOP,
      DeviceType.TABLET,
      DeviceType.MOBILE,
    ]

    // A bit hacky, but to make sure that the layout section has the right width for each device, we resize it on each one
    const SCREEN_SIZES = {
      mobile: DeviceWidth.MOBILE_DEFAULT_WIDTH,
      tablet: DeviceWidth.TABLET_DEFAULT_WIDTH,
      desktop: DeviceWidth.DESKTOP_DEFAULT_WIDTH,
    }

    const innerSectionChildren = getContainerChildrenFromSection(newObject)
    const hasInnerSectionChildren =
      innerSectionChildren && innerSectionChildren.length > 0

    if (hasInnerSectionChildren) {
      // Create the layout section with children

      const newLayoutSection = createLayoutSectionFromPrebuilt(newObject)

      const outerSection = getLayoutHelperFromSection(newObject)
      const innerSection = getContainerFromLayoutHelper(outerSection)
      const { id: outerSectionId } = outerSection
      const { id: innerSectionId } = innerSection

      const newList = update(list, map[newLayoutSection.id], newLayoutSection)
      list = newList
      const [list1, map1] = changeObjectParent(
        newList,
        map,
        outerSectionId,
        newLayoutSection.id
      )

      const [list2, map2] = changeObjectParent(
        list1,
        map1,
        innerSectionId,
        outerSectionId
      )

      list = list2
      map = map2

      const fitForScreen = getLayoutSectionFitForScreen(component, true)
      const instructions = []

      // First we need to move the layout section to the right position.
      // The "outerSection" is the first children in the layout section, the layout helper,
      // we move it as well to match the layout section position, though this is a bit of a patch as
      // just moving the layout section should be enough for it's children to move accordingly,
      // but at the moment both moveElements are needed, otherwise the layout helper won't be positioned correctly.
      const newObjectY = newObject[currentDevice]?.y ?? newObject.y
      instructions.push(moveElement(newObject.id, newObject.x, newObjectY))

      const outerSectionY = outerSection[currentDevice]?.y ?? outerSection.y
      instructions.push(moveElement(outerSectionId, newObject.x, outerSectionY))

      for (const device of allDevices) {
        const deviceObject = getDeviceObject(newObject, device)
        const screenWidthForDevice =
          device === currentDevice ? screenWidth : SCREEN_SIZES[device]

        const x = fitForScreen[device]?.x ?? deviceObject.x
        const width = fitForScreen[device]?.width ?? screenWidthForDevice

        instructions.push(
          resizeScreen(component.id, screenWidthForDevice, screenHeight)
        )

        instructions.push(moveElement(newObject.id, x, deviceObject.y))
        const outerSectionDeviceY = getDeviceObject(outerSection, device).y
        instructions.push(moveElement(outerSectionId, x, outerSectionDeviceY))

        instructions.push(
          resizeElement(newObject.id, width, deviceObject.height)
        )
      }

      // set screen back to original size
      instructions.push(resizeScreen(component.id, screenWidth, screenHeight))
      ;({ list } = applyInstructions({ list, pathMap: map }, instructions))
    } else {
      // Create a new empty layout section

      const outerSectionId = uuid4()
      const innerSectionId = uuid4()

      newObject = createDefaultLayoutSection(newObject, {
        screenWidth,
        outerSectionId,
        innerSectionId,
      })

      const newList = update(list, map[newObject.id], newObject)

      const [list1, map1] = changeObjectParent(
        newList,
        map,
        outerSectionId,
        newObject.id
      )

      const [list2, map2] = changeObjectParent(
        list1,
        map1,
        innerSectionId,
        outerSectionId
      )

      list = list2
      map = map2

      const fitForScreen = getLayoutSectionFitForScreen(component, true)
      const hasDeviceSpecificFit = Object.keys(fitForScreen).some(k =>
        allDevices.includes(k)
      )

      if (hasDeviceSpecificFit) {
        const outerSection = getLayoutHelperFromSection(newObject)
        const instructions = []
        for (const device of allDevices) {
          if (fitForScreen) {
            const x = fitForScreen[device]?.x
            const width = fitForScreen[device]?.width

            if (x || width) {
              // Need to enable device-specific layout before running a move or resize instruction
              instructions.push(
                enableDeviceSpecificLayout(newObject.id, device)
              )
            }

            const screenWidthForDevice =
              device === currentDevice ? screenWidth : SCREEN_SIZES[device]

            const heightForDevice =
              newObject[device]?.height ?? newObject.height

            instructions.push(
              resizeScreen(component.id, screenWidthForDevice, screenHeight)
            )

            if (x) {
              instructions.push(moveElement(newObject.id, x, newObject.y))
              const outerSectionY = outerSection[device]?.y ?? outerSection.y
              instructions.push(moveElement(outerSectionId, x, outerSectionY))
            }

            if (width) {
              instructions.push(
                resizeElement(newObject.id, width, heightForDevice)
              )
            }
          }
        }

        // set screen back to original size
        instructions.push(resizeScreen(component.id, screenWidth, screenHeight))
        ;({ list } = applyInstructions({ list, pathMap: map }, instructions))
      }
    }
  }

  // in case we have newly created screens
  // (such as one that was pasted into the app or simply created in the editor from the templates)
  // we need to check if when in "mobile only" mode, if the screen added
  // exceeds the width limit for mobile devices
  for (const componentId of componentIds) {
    const component = getObject(list, map[componentId])

    if (mobileOnly && component.width >= DeviceBreakpoint.MOBILE_BREAKPOINT) {
      const instructions = []

      instructions.push(
        resizeScreen(
          component?.id,
          DeviceWidth.MOBILE_DEFAULT_WIDTH,
          component.height
        )
      )
      ;({ list } = applyInstructions({ list, pathMap: map }, instructions))
    }

    saveTouched(
      state.appId,
      list,
      map,
      newObjects.map(o => o.id),
      undefined,
      undefined,
      libraryGlobals
    )
  }

  const result = [
    updateIndices({
      ...state,
      list,
      map,
      zoom,
      libraryGlobals,
      selection: setSelection ? selection : state.selection,
      activeComponent: getActiveComponent(list, map, selection),
      shapeEditing: newObject.type === SHAPE && newObject.id,
    }),
    ...newIds,
  ]

  return result
}

const getRelativePosition = (object, parent) => {
  if (!object || !parent) {
    return 0
  }

  const parentWidth = parent.width

  let parentX = 0
  if ([GROUP, LIST].includes(parent.type)) {
    parentX = parent.x || 0
  }

  const { x, width } = object
  const center = x - parentX + width / 2

  return center / parentWidth
}

const calculateEndResizePosition = (object, newParent, oldParent) => {
  if (!object || !newParent || !oldParent) {
    return {}
  }

  let oldParentX = 0
  if ([GROUP, LIST].includes(oldParent.type)) {
    oldParentX = oldParent.x || 0
  }
  let newParentX = 0
  if ([GROUP, LIST].includes(newParent.type)) {
    newParentX = newParent.x || 0
  }

  let newX = object.x - oldParentX

  if (newParent.width === oldParent.width) {
    return { newX: newX + newParentX }
  }

  const { responsivity, width } = object
  const { horizontalScaling, horizontalPositioning } = responsivity || {}
  let relativeWidth = object.width / oldParent.width
  const parentInitialWidth = oldParent.width
  const relativePosition = getRelativePosition(object, oldParent)

  const alignment = getObjectAlignment(object)
  let padding
  if (
    horizontalScaling === SCALES_WITH_PARENT &&
    alignment === LEFT_AND_RIGHT
  ) {
    padding = oldParent.width - object.width
    relativeWidth = `calc(100% - ${padding / 2}px)`
  }

  let newWidth = width
  const parentWidth = newParent.width

  if (horizontalScaling === SCALES_WITH_PARENT) {
    newWidth = relativeWidth * parentWidth
  }

  switch (horizontalPositioning) {
    case LEFT_AND_RIGHT:
      newWidth = parentWidth - padding

      break
    case LEFT:
      break
    case RIGHT:
      newX += parentWidth - parentInitialWidth + width - newWidth

      break
    case CENTER:
    default:
      newX = relativePosition * parentWidth - newWidth / 2

      break
  }

  return { newX: newX + newParentX, newWidth }
}

const updateResponsivePosition = (object, newParent, oldParent) => {
  const { newX, newWidth } = calculateEndResizePosition(
    object,
    newParent,
    oldParent
  )

  return {
    ...object,
    x: newX !== undefined ? newX : object.x,
    width: newWidth !== undefined ? newWidth : object.width,
  }
}

const isPropChanging = (propKey, oldObject, changes) => {
  if (!propKey || !oldObject || !changes) {
    return false
  }

  const propGettingSet = propKey in changes && changes[propKey] !== undefined
  const propValueDifferent = oldObject[propKey] !== changes[propKey]

  return propGettingSet && propValueDifferent
}

/**
 * @typedef PositionSizeChange
 * @property {number} [x]
 * @property {number} [y]
 * @property {number} [width]
 * @property {number} [height]
 */

/**
 *
 * @param {import('utils/responsiveTypes').EditorObject} oldDeviceObject
 * @param {PositionSizeChange} changes
 * @returns {import('./instructions/applyInstructions').Instruction[]}
 */
const inferInstructions = (oldDeviceObject, changes) => {
  const { id: objectId, type } = oldDeviceObject

  const actualChanges = {}

  for (const propKey in changes) {
    if (isPropChanging(propKey)) {
      actualChanges[propKey] = changes[propKey]
    }
  }

  changes = coerceScreenResizeToMinimums(oldDeviceObject, changes)

  const result = []
  if ('x' in changes || 'y' in changes) {
    const { x = oldDeviceObject.x, y = oldDeviceObject.y } = changes

    if (type === COMPONENT) {
      result.push(moveScreen(objectId, x, y))
    } else {
      result.push(moveElement(objectId, x, y))
    }
  }

  if ('width' in changes || 'height' in changes) {
    const { width = oldDeviceObject.width, height = oldDeviceObject.height } = changes // prettier-ignore

    if (type === COMPONENT) {
      result.push(resizeScreen(objectId, width, height))
    } else {
      const { constrainMinWidth, constrainMaxWidth } = getWidthConstraints(
        oldDeviceObject,
        width
      )

      const { y = oldDeviceObject.y } = changes

      if (constrainMinWidth === true) {
        // NOTE: early-out!
        // disregard any incoming move changes to prevent drift
        // by forcing the width to the min width and position to existing position
        return [
          resizeElement(objectId, oldDeviceObject.minWidth, height),
          moveElement(objectId, oldDeviceObject.x, y),
        ]
      } else if (constrainMaxWidth === true) {
        // NOTE: early-out!
        // disregard any incoming move changes to prevent drift
        // by forcing the width to the max width and position to existing position
        return [
          resizeElement(objectId, oldDeviceObject.maxWidth, height),
          moveElement(objectId, oldDeviceObject.x, y),
        ]
      } else {
        result.push(resizeElement(objectId, width, height))
      }
    }
  }

  return result
}

export const calcChildBoundingBoxReducer = (boundingBox, currentChild) => {
  const currentRight = currentChild.x + currentChild.width
  const currentBottom = currentChild.y + currentChild.height

  return {
    left: Math.min(boundingBox.left, currentChild.x),
    right: Math.max(boundingBox.right, currentRight),
    top: Math.min(boundingBox.top, currentChild.y),
    bottom: Math.max(boundingBox.bottom, currentBottom),
  }
}

const repositionChildren = (newParent, oldParent) => {
  if (!newParent.children || !newParent.children.length) {
    return newParent
  }

  const diffWidth = newParent.width - oldParent.width
  const diffX = newParent.x - oldParent.x

  if (!diffWidth && !diffX) {
    return newParent
  }

  const repositioned = { ...newParent }

  repositioned.children = repositioned.children.map(child => {
    const newChild = updateResponsivePosition(child, newParent, oldParent)

    return repositionChildren(newChild, child)
  })

  let bboxUpdates = {}
  if ([GROUP].includes(newParent.type) && repositioned.children.length > 0) {
    const { left, right, top, bottom } = repositioned.children.reduce(
      calcChildBoundingBoxReducer,
      {
        left: Number.MAX_SAFE_INTEGER,
        right: Number.MIN_SAFE_INTEGER,
        top: Number.MAX_SAFE_INTEGER,
        bottom: Number.MIN_SAFE_INTEGER,
      }
    )
    const newWidth = right - left
    const newHeight = bottom - top

    bboxUpdates = {
      x: left,
      y: top,
      width: newWidth,
      height: newHeight,
    }
  }

  return {
    ...repositioned,
    ...bboxUpdates,
  }
}

/**
 *
 * @param {object} state
 * @param {string | string[]} id
 * @returns {typeof INITIAL_STATE}
 */
export const performDelete = (state, id) => {
  let { list, map } = state

  const object = getObject(state.list, state.map[id])

  if (isContainerSectionElement(object)) {
    const parentSection = getSectionFromSectionElement(
      state.list,
      state.map,
      object
    )

    return performDelete(state, parentSection.id)
  }

  if (!Array.isArray(id)) {
    id = [id]
  }
  const { magicLayout } = state

  const paths = []
  const componentIds = []

  id.forEach(id => {
    const path = map[id]
    const obj = getObject(list, path)

    if (obj.type === COMPONENT) {
      componentIds.push(id)
    } else {
      paths.push(path)
    }

    list = remove(list, path)

    list = updateParentBounds(
      list,
      map,
      id,
      null,
      resizeParent,
      undefined,
      magicLayout
    )
    list = updateParentOptions(list, map, id)

    map = { ...map }
    delete map[id]
    map = remapChildren(object, map, path)
    map = remapSiblings(list, map, subPath(path, path.length - 1))

    const pieces = path.split('.')
    const parentPath = pieces.slice(0, pieces.length - 1).join('.')
    let siblings = []

    if (parentPath === '') {
      siblings = list
    } else {
      const parentObj = getObject(list, parentPath)

      siblings = (parentObj && parentObj.children) || []
    }

    const pathPrefix = parentPath === '' ? '' : `${parentPath}.`

    siblings.forEach((sibling, position) => {
      map[sibling.id] = `${pathPrefix}${position}`
    })
  })

  const changedScreenIds = []
  paths.forEach(path => {
    const parentScreen = getObject(list, subPath(path, 1))

    if (
      !componentIds.includes(parentScreen) &&
      !changedScreenIds.includes(parentScreen.id)
    ) {
      changedScreenIds.push(parentScreen?.id)
    }
  })

  if (magicLayout) {
    for (const componentId of changedScreenIds) {
      list = calculatePushGraphs(list, map, componentId)
    }
  }

  saveTouched(state.appId, list, map, null, paths, componentIds)

  return updateIndices({
    ...state,
    list,
    map,
    selection: [],
    hoverSelection: [],
    shapeEditing: null,
  })
}

const remapChildren = (object, map, path) => {
  if (!Array.isArray(object?.children) || object?.children?.length === 0) {
    return map
  }

  let newMap = { ...map }

  for (const child of object.children) {
    const childPath = map[child.id]

    if (childPath.startsWith(path)) {
      delete newMap[child.id]
    }

    if (child.children) {
      newMap = remapChildren(child, newMap, childPath)
    }
  }

  return newMap
}

const updateIndices = state => {
  const componentPaths = Object.keys(state.list)

  const typeIndex = { ...state.typeIndex }

  componentPaths.forEach(path => {
    const component = state.list[path]

    typeIndex[component.id] = indexByType(component.children)
  })

  return {
    ...state,
    typeIndex,
  }
}

const addMissingPushGraphsToScreens = (list, map) => {
  const screensWithoutPushGraphs = list
    .filter(({ type, pushGraph }) => type === COMPONENT && !pushGraph)
    .map(({ id }) => id)

  for (const screenId of screensWithoutPushGraphs) {
    list = calculatePushGraphs(list, map, screenId)
  }

  return list
}

// REDUCER

export default mergeReducers(
  INITIAL_STATE,
  [snappingMiddleware],
  selectionReducer,
  textEditingReducer,
  shapeEditingReducer,
  sketchUploadReducer,
  positioningReducer,
  clipboardReducer,
  snappingReducer,

  (state, action) => {
    if (DISABLED_ACTIONS_FOR_SECTION_ELEMENTS.has(action.type) && action.id) {
      const object = getObject(state.list, state.map[action.id])

      if (object && isSectionElement(object)) {
        return state
      }
    }

    if (action.type === REQUEST_DATA) {
      const { appId } = action

      // Make call to API
      requestApp(appId)

      if (appId === state.appId) {
        return { ...state, loading: true }
      }

      setPrevList(null)

      return {
        ...INITIAL_STATE,
        appId,
        loading: true,
      }
    }

    if (action.type === SET_DATA) {
      const { appId, data, app } = action

      let list = Object.keys(data || {}).map(id => getComponentObject(data, id))

      const libraryGlobals = app.libraryGlobals || {}
      const map = remapSiblings(list, {}, '0')

      const { magicLayout = false } = app || {}

      // Initialize push graphs
      if (magicLayout) {
        list = addMissingPushGraphsToScreens(list, map)
      }

      let zoom = state.zoom

      if (state.zoomAppId !== appId) {
        // focus on launch component
        let launchComponent = list.find(
          ({ id }) => app.launchComponentId === id
        )

        if (!launchComponent && list.length > 0) {
          launchComponent = list[0]
        }

        if (launchComponent) {
          let { x, y, width, height } = launchComponent
          const padding = 400
          x = x - padding
          y = y - padding
          width = width + 2 * padding
          height = height + 2 * padding

          zoom = calculateZoom({ x, y, width, height })
        } else {
          zoom = calculateZoom(null)
        }
      }

      setPrevList(list)

      let newState = updateIndices({
        ...state,
        appId,
        list,
        map,
        zoom,
        magicLayout,
        libraryGlobals,
        loading: false,
      })

      newState = {
        ...newState,
        ...getSnapGrid(newState, []),
      }

      return newState
    }

    if (action.type === CREATE_OBJECT) {
      const { path, object, id, silent, mouseCoords, globalState, parentId } =
        action

      // Only include initial device in objects when the hasAutoCustomLayout flag is set
      const includeInitialDevice = getFeatureFlag(
        globalState,
        'hasAutoCustomLayout'
      )
      const hasUpdatedGroups = getFeatureFlag(globalState, 'hasUpdatedGroups')

      const hasSectionComponent = getFeatureFlag(
        globalState,
        'hasSectionComponent'
      )

      const hasNewMobileOnlyApp = getFeatureFlag(
        globalState,
        'hasNewMobileOnlyApp'
      )

      const hasMinMaxWidth = getFeatureFlag(globalState, 'hasMinMaxWidth')

      const app = getApp(globalState, state.appId)
      const mobileOnly =
        hasNewMobileOnlyApp && app?.webSettings?.layoutMode === 'mobile'

      return performCreate(
        state,
        path,
        object,
        id,
        parentId ?? null,
        false,
        false,
        !silent,
        undefined,
        includeInitialDevice,
        hasUpdatedGroups,
        mobileOnly,
        hasSectionComponent,
        mouseCoords,
        hasMinMaxWidth
      )[0]
    }

    if ([UPDATE_OBJECT, RESIZE_OBJECT].indexOf(action.type) !== -1) {
      const { id, object, skipSave, globalState, resetAccordion } = action

      const path = state.map[id]
      if (!path) return state
      const { magicLayout, libraryGlobals } = state

      const hasAutoCustomLayout = getFeatureFlag(
        globalState,
        'hasAutoCustomLayout'
      )

      /** @type {import('utils/responsiveTypes').EditorObject} */
      const oldObject = getObject(state.list, path) || {}
      const changes = evaluate(object, oldObject)

      // Sometimes UPDATE_OBJECT changes include width and height changes
      // which should be run through our responsive layout calculations
      const updateObjectHasResizeChanges =
        // TODO(michael-adalo): deliberately scoping this to FORM objects for now
        oldObject.type === FORM &&
        action.type === UPDATE_OBJECT &&
        ('width' in changes || 'height' in changes)

      evaluateUserflowEvents(
        [USERFLOW_EVENTS.LIST_CONNECTED, USERFLOW_EVENTS.LINK_ACTION_CREATED],
        state.appId,
        {
          object,
          oldObject,
        }
      ).catch(console.error)

      /** @type {import('./types/ObjectList').ObjectList} */
      let list = state.list

      if (
        magicLayout &&
        (action.type === RESIZE_OBJECT || updateObjectHasResizeChanges === true)
      ) {
        const component = getObject(list, subPath(path, 1))
        const screenWidth = component.width
        const device = getDeviceType(screenWidth)
        const oldDeviceObject = getDeviceObject(oldObject, device)

        const section = getSectionFromChild(list, state.map, oldDeviceObject)
        const parentSelection = section ? [section.id] : []

        /** @type {import('./instructions/applyInstructions').Instruction[]} */
        const instructions = []

        if (updateObjectHasResizeChanges === true) {
          const { width, height, ...otherChanges } = changes
          instructions.push(updateElement(oldObject.id, otherChanges))
        }

        instructions.push(...inferInstructions(oldDeviceObject, changes))

        if (
          hasAutoCustomLayout &&
          oldObject.type !== COMPONENT &&
          oldDeviceObject.initialDevice !== device
        ) {
          // With Auto Custom Layout on, before a layout change in a device different from the initial one happens, we enable device-specific layout in that device
          instructions.unshift(enableDeviceSpecificLayout(oldObject.id, device))
        }

        ;({ list } = applyInstructions(
          {
            list,
            pathMap: state.map,
            featureFlags: globalState.featureFlags,
          },
          instructions
        ))

        const hasNewMobileOnlyApp = getFeatureFlag(globalState, 'hasNewMobileOnlyApp') // prettier-ignore
        const app = getApp(globalState, state.appId)
        const mobileOnly = hasNewMobileOnlyApp && app?.webSettings?.layoutMode === 'mobile' // prettier-ignore

        // in case we have newly created screens
        // (such as one that was pasted into the app or simply created in the editor from the templates)
        // we need to check if when in "mobile only" mode, if the screen added
        // exceeds the width limit for mobile devices
        if (
          mobileOnly &&
          component.width >= DeviceBreakpoint.MOBILE_BREAKPOINT
        ) {
          ;({ list } = applyInstructions({ list, pathMap: state.map }, [
            resizeScreen(
              component?.id,
              DeviceWidth.MOBILE_DEFAULT_WIDTH,
              component.height
            ),
          ]))
        }

        if (!skipSave) {
          // Only the parent screen is necessary here as saves will include all objects on the screen.
          const changedObjects = [id]
          saveTouched(state.appId, list, state.map, changedObjects)
        }

        // MAINTAIN DEFAULT RESIZE_OBJECT BEHAVIOUR
        if (updateObjectHasResizeChanges === false) {
          return { ...state, list, parentSelection }
        }

        // FALL-THROUGH TO REMAINED OF UPDATE_OBJECT BEHAVIOUR
      }

      if (updateObjectHasResizeChanges === true) {
        delete changes.width
        delete changes.height
      }

      let newObject = deepMerge(oldObject, changes)

      if (oldObject.type !== COMPONENT) {
        newObject = updateObjectWidth(newObject)
        newObject = translateChildren(newObject, oldObject)
      } else if (magicLayout) {
        newObject = repositionChildren(newObject, oldObject)
      }

      if (oldObject.type === LIBRARY_COMPONENT) {
        const { libraryName, componentName } = newObject

        const globals = libraryGlobals[libraryName]?.[componentName]

        const openAccordion = resetAccordion
          ? null
          : getAccordionState(globalState, LIBRARY_INSPECT_GROUP)

        const component = getObject(list, subPath(path, 1))

        newObject = updateComponentBounds(
          newObject,
          globals,
          openAccordion,
          component
        )
      }

      if (
        oldObject.type === LABEL &&
        'width' in object &&
        oldObject.width !== object.width
      ) {
        newObject.autoWidth = false
      }

      list = update(list, path, newObject)
      list = updateParentBounds(
        list,
        state.map,
        id,
        null,
        resizeParent,
        undefined,
        magicLayout
      )
      list = updateOptions(list, state.map, id)

      if (magicLayout) {
        const screen = getObject(list, subPath(path, 1))

        list = calculatePushGraphs(list, state.map, screen.id)

        if (updateObjectHasResizeChanges === true) {
          ;({ list } = applyInstructions({ list, pathMap: state.map }, [
            resizeScreen(screen.id, screen.width, screen.height),
          ]))
        }
      }

      if (!skipSave) {
        saveTouched(state.appId, list, state.map, [id])
      }

      return {
        ...state,
        list,
      }
    }

    if (action.type === UPDATE_OBJECTS) {
      const { objects, globalState } = action
      const { magicLayout } = state

      let list = state.list

      const hasAutoCustomLayout = getFeatureFlag(
        globalState,
        'hasAutoCustomLayout'
      )

      for (const object of objects) {
        const { id } = object
        const path = state.map[id]

        const oldObject = getObject(state.list, path)
        const changes = evaluate(object, oldObject)
        const resizing = 'width' in changes || 'height' in changes

        evaluateUserflowEvents(
          [USERFLOW_EVENTS.LIST_CONNECTED, USERFLOW_EVENTS.LINK_ACTION_CREATED],
          state.appId,
          {
            object,
            oldObject,
          }
        ).catch(console.error)

        // TODO(enzo): PATCH to stop columnCount updates from running a resize
        const isListColumnsUpdate = objects.some(o => o.columnCount)

        if (magicLayout && resizing && !isListColumnsUpdate) {
          const component = getObject(list, subPath(path, 1))
          const screenWidth = component.width
          const device = getDeviceType(screenWidth)
          const oldDeviceObject = getDeviceObject(oldObject, device)

          const instructions = inferInstructions(oldDeviceObject, changes)

          const hasLayoutInstruction = instructions.some(i =>
            ['moveElement', 'resizeElement'].includes(i.operation)
          )

          if (
            hasAutoCustomLayout &&
            oldObject.type !== COMPONENT &&
            hasLayoutInstruction &&
            oldDeviceObject.initialDevice !== device
          ) {
            // With Auto Custom Layout on, before a layout change in a device different from the initial one happens, we enable device-specific layout in that device
            instructions.unshift(
              enableDeviceSpecificLayout(oldObject.id, device)
            )
          }

          const { list: updatedList } = applyInstructions(
            {
              list,
              pathMap: state.map,
              featureFlags: globalState.featureFlags,
            },
            instructions
          )

          list = updatedList
        } else {
          let newObject = { ...oldObject, ...changes }

          if (oldObject.type !== COMPONENT) {
            newObject = updateObjectWidth(newObject)
            newObject = translateChildren(newObject, oldObject)
          }

          if (oldObject.type === LIBRARY_COMPONENT) {
            const openAccordion = getAccordionState(
              globalState,
              LIBRARY_INSPECT_GROUP
            )

            const component = getObject(state.list, subPath(path, 1))

            newObject = updateComponentBounds(
              newObject,
              null,
              openAccordion,
              component
            )
          }
          list = update(list, path, newObject)
          list = updateParentBounds(
            list,
            state.map,
            id,
            null,
            resizeParent,
            undefined,
            magicLayout
          )
          list = updateOptions(list, state.map, id)
        }
      }

      saveTouched(
        state.appId,
        list,
        state.map,
        objects.map(obj => obj.id)
      )

      return {
        ...state,
        list,
      }
    }

    if (action.type === UPDATE_OBJECT_BOUNDS) {
      const { id, globalState, resetAccordion } = action
      const { libraryGlobals, magicLayout } = state
      let { list, map } = state

      const path = map[id]
      if (!path) {
        return state
      }

      const oldObject = getObject(list, path) || {}
      const { type, libraryName, componentName } = oldObject

      if (type !== LIBRARY_COMPONENT) {
        return state
      }

      const globals = libraryGlobals[libraryName]?.[componentName]

      const openAccordion = resetAccordion
        ? null
        : getAccordionState(globalState, LIBRARY_INSPECT_GROUP)

      const component = getObject(state.list, subPath(path, 1))

      const newObject = updateComponentBounds(
        oldObject,
        globals,
        openAccordion,
        component
      )

      list = update(list, path, newObject)

      list = updateParentBounds(
        list,
        state.map,
        id,
        null,
        resizeParent,
        undefined,
        magicLayout
      )
      list = updateOptions(list, state.map, id)

      if (magicLayout) {
        const screen = getObject(list, subPath(path, 1))
        list = calculatePushGraphs(list, state.map, screen.id)
      }

      return {
        ...state,
        list,
      }
    }

    // TODO (michael-adalo): appears to be unused?
    if (action.type === CHANGE_OBJECT_TYPE) {
      const { id, newType } = action
      const { magicLayout } = state

      const path = state.map[id]
      let object = getObject(state.list, path)
      let { selection, map } = state

      if (newType === INPUT) {
        object = convertToInput(object)
      } else if (newType === LIST) {
        let children = [object]

        if (object.type === GROUP) {
          children = object.children || []
        }

        object = createEmptyObject(state.list, map, {
          children,
          height: 400,
          type: LIST,
        })

        selection = [object.id]
      } else {
        object = convertType(object, newType)
      }

      let list = update(state.list, path, object)
      map = remapSiblings(list, map, '0')

      if (object.id !== id) {
        list = updateParentBounds(
          list,
          map,
          object.children[0].id,
          null,
          resizeParent,
          undefined,
          magicLayout
        )
      }

      if (newType === LIST) {
        const { id } = object
        let obj = getObject(list, map[id])
        obj = { ...obj, height: obj.height * 3 }
        list = update(list, map[id], obj)
      }

      saveTouched(state.appId, list, map, [id])

      return updateIndices({
        ...state,
        selection,
        map,
        list,
      })
    }

    if (action.type === POSITION_OBJECTS) {
      const { ids, offset, shouldSave, globalState } = action

      const { magicLayout } = state
      let list = state.list

      const objects = getSelectedSub(state, ids)

      const hasAutoCustomLayout = getFeatureFlag(
        globalState,
        'hasAutoCustomLayout'
      )

      for (const object of objects) {
        if (magicLayout) {
          // transform section elements (layout_helper/container) into layout_section
          // non-section-related elements will return original object
          const obj = getSectionFromSectionElement(list, state.map, object)

          const screen = getParentScreen(list, state.map, obj.id)
          const device = screen ? getDeviceType(screen.width) : undefined
          const { x, y } = getDeviceObject(obj, device)

          const finalX = x + offset.x
          const finalY = y + offset.y

          const instructions = []
          if (obj.type === COMPONENT) {
            instructions.push(moveScreen(obj.id, finalX, finalY))
          } else {
            if (hasAutoCustomLayout && device !== obj.initialDevice) {
              // With Auto Custom Layout on, before a layout change in a device different from the initial one happens, we enable device-specific layout in that device
              instructions.push(enableDeviceSpecificLayout(obj.id, device))
            }
            instructions.push(moveElement(obj.id, finalX, finalY))
          }

          ;({ list } = applyInstructions(
            {
              list,
              pathMap: state.map,
              featureFlags: globalState.featureFlags,
            },
            instructions
          ))
        } else {
          let newObj = {
            ...object,
            x: object.x + offset.x,
            y: object.y + offset.y,
          }

          if (object.type !== COMPONENT) {
            newObj = translateChildren(newObj, object)
          }

          list = update(list, state.map[object.id], newObj)
          list = updateParentBounds(
            list,
            state.map,
            object.id,
            null,
            resizeParent
          )
        }
      }

      if (shouldSave) {
        saveTouched(state.appId, list, state.map, ids)
      }

      return {
        ...state,
        list,
      }
    }

    if (action.type === ALIGN_OBJECTS) {
      const { selection, appId, map } = state
      let { list, magicLayout } = state
      const { direction } = action
      const objects = selection.map(id => getObject(list, map[id]))
      const absoluteObjects = objects.map(o => getAbsoluteBbox(o, list, map))

      const bbox =
        objects.length === 1
          ? getObject(list, subPath(map[objects[0].id], 1))
          : getBoundingBox(absoluteObjects)

      objects.forEach((obj, i) => {
        let newObj = alignToRect(obj, absoluteObjects[i], bbox, direction)

        if (newObj.type !== COMPONENT) {
          newObj = translateChildren(newObj, obj)
        }

        list = update(list, map[obj.id], newObj)
        list = updateParentBounds(
          list,
          map,
          obj.id,
          null,
          resizeParent,
          undefined,
          magicLayout
        )
      })

      saveTouched(appId, list, map, selection)

      return {
        ...state,
        list,
      }
    }

    if (action.type === DELETE_OBJECT) {
      const { id } = action

      return performDelete(state, id)
    }

    if (action.type === REORDER_OBJECTS) {
      const { ids, dropTarget, options } = action
      let { list, map, magicLayout } = state

      const { dropAfter, dropInside } = options

      let dropPath = map[dropTarget]
      const affectedScreenPaths = [subPath(dropPath, 1)]

      if (dropInside) {
        const children = getObject(list, dropPath).children

        if (!children) {
          list = update(list, dropPath, {
            ...getObject(list, dropPath),
            children: [],
          })
        }

        const childCount = children ? children.length : 0
        dropPath = joinPaths(dropPath, `${childCount}`)
        affectedScreenPaths.push(subPath(dropPath, 1))
      }

      if (!dropAfter) {
        dropPath = incrementPath(dropPath)
      }

      let itemPaths = ids.map(id => map[id]).filter(i => i)

      const itemParentPaths = itemPaths.map(path => subPath(path, 1))
      affectedScreenPaths.concat(itemParentPaths)

      for (let i = 0; i < itemPaths.length; i += 1) {
        const path = itemPaths[i]

        if (isChildPath(path, dropPath)) {
          return state
        }
      }

      itemPaths = sortPaths(itemPaths)
      const items = itemPaths.map(path => getObject(list, path))

      dropPath = getDropPath(dropPath, itemPaths)

      itemPaths
        .slice()
        .reverse()
        .forEach(path => {
          list = remove(list, path)
          list = updateParentBounds(
            list,
            map,
            null,
            path,
            resizeParent,
            undefined,
            magicLayout
          )
          list = updateParentOptions(list, map, null, path)
        })

      list = insert(list, dropPath, ...items)
      map = remapSiblings(list, map, '0')

      items.forEach(itm => {
        list = updateParentBounds(
          list,
          map,
          itm.id,
          null,
          resizeParent,
          undefined,
          magicLayout
        )
        list = updateOptions(list, map, itm.id)
      })

      // Section rule: it's not possible for a Section to be a child of another component
      for (const id of ids) {
        const object = getObject(list, map[id])
        const objectParent = getParent(list, map, id)

        if (object.type === LAYOUT_SECTION && objectParent.type !== COMPONENT) {
          return state
        }
      }

      // Section rule: it's not possible for a component to be dropped alongside a layout helper or container
      const siblings = getSiblings(list, dropPath)
      for (const sibling of siblings) {
        if (isSectionElement(sibling)) {
          return state
        }
      }

      if (magicLayout) {
        const affectedScreenIds = affectedScreenPaths
          .map(screenPath => {
            const screen = getObject(list, screenPath)

            return screen?.id
          })
          .filter(id => id !== undefined)
        const uniqueScreenIds = Array.from(new Set(affectedScreenIds))
        for (const screenId of uniqueScreenIds) {
          list = calculatePushGraphs(list, map, screenId)
        }
      }

      const affectedPaths = itemPaths.concat([dropPath, ...affectedScreenPaths])
      saveTouched(state.appId, list, map, null, affectedPaths)

      return {
        ...state,
        list,
        map,
        hoverSelection: [],
      }
    }

    if (action.type === REORDER_OBJECTS_MOVE_FIRST) {
      const { ids } = action
      let { list, map, magicLayout } = state

      const actualPath = map[ids]

      let dropPath = `${subPath(actualPath, pathLength(actualPath) - 1)}.9999`

      let itemPaths = ids.map(id => map[id]).filter(i => i)

      for (let i = 0; i < itemPaths.length; i += 1) {
        const path = itemPaths[i]

        if (isChildPath(path, dropPath)) {
          return state
        }
      }

      itemPaths = sortPaths(itemPaths)
      const items = itemPaths.map(path => getObject(list, path))

      dropPath = getDropPath(dropPath, itemPaths)

      itemPaths
        .slice()
        .reverse()
        .forEach(path => {
          list = remove(list, path)
          list = updateParentBounds(
            list,
            map,
            null,
            path,
            resizeParent,
            undefined,
            magicLayout
          )
          list = updateParentOptions(list, map, null, path)
        })

      list = insert(list, dropPath, ...items)
      map = remapSiblings(list, map, '0')

      items.forEach(itm => {
        list = updateParentBounds(
          list,
          map,
          itm.id,
          null,
          resizeParent,
          undefined,
          magicLayout
        )
        list = updateOptions(list, map, itm.id)
      })

      const effectedPaths = itemPaths.concat([dropPath])
      saveTouched(state.appId, list, map, null, effectedPaths)

      return {
        ...state,
        list,
        map,
        hoverSelection: [],
      }
    }

    if (action.type === REORDER_OBJECTS_MOVE_LAST) {
      const { ids } = action
      let { list, map, magicLayout } = state

      const actualPath = map[ids]

      let dropPath = `${subPath(actualPath, pathLength(actualPath) - 1)}.0`

      const lastLayerCheck = actualPath === dropPath

      if (lastLayerCheck) {
        return state
      }

      let itemPaths = ids.map(id => map[id]).filter(i => i)

      for (let i = 0; i < itemPaths.length; i += 1) {
        const path = itemPaths[i]

        if (isChildPath(path, dropPath)) {
          return state
        }
      }

      itemPaths = sortPaths(itemPaths)
      const items = itemPaths.map(path => getObject(list, path))

      dropPath = getDropPath(dropPath, itemPaths)

      itemPaths
        .slice()
        .reverse()
        .forEach(path => {
          list = remove(list, path)
          list = updateParentBounds(
            list,
            map,
            null,
            path,
            resizeParent,
            undefined,
            magicLayout
          )
          list = updateParentOptions(list, map, null, path)
        })

      list = insert(list, dropPath, ...items)
      map = remapSiblings(list, map, '0')

      items.forEach(itm => {
        list = updateParentBounds(
          list,
          map,
          itm.id,
          null,
          resizeParent,
          undefined,
          magicLayout
        )
        list = updateOptions(list, map, itm.id)
      })

      const effectedPaths = itemPaths.concat([dropPath])
      saveTouched(state.appId, list, map, null, effectedPaths)

      return {
        ...state,
        list,
        map,
        hoverSelection: [],
      }
    }

    if (action.type === REORDER_OBJECTS_MOVE_UP) {
      const { ids } = action
      let { list, map, magicLayout } = state

      const actualPath = map[ids]

      if (!actualPath) {
        return state
      }

      let dropPath = incrementPath(actualPath)
      dropPath = incrementPath(dropPath)

      let itemPaths = ids.map(id => map[id]).filter(i => i)

      for (let i = 0; i < itemPaths.length; i += 1) {
        const path = itemPaths[i]

        if (isChildPath(path, dropPath)) {
          return state
        }
      }

      itemPaths = sortPaths(itemPaths)
      const items = itemPaths.map(path => getObject(list, path))

      dropPath = getDropPath(dropPath, itemPaths)

      itemPaths
        .slice()
        .reverse()
        .forEach(path => {
          list = remove(list, path)
          list = updateParentBounds(
            list,
            map,
            null,
            path,
            resizeParent,
            undefined,
            magicLayout
          )
          list = updateParentOptions(list, map, null, path)
        })

      list = insert(list, dropPath, ...items)
      map = remapSiblings(list, map, '0')

      items.forEach(itm => {
        list = updateParentBounds(
          list,
          map,
          itm.id,
          null,
          resizeParent,
          undefined,
          magicLayout
        )
        list = updateOptions(list, map, itm.id)
      })

      const effectedPaths = itemPaths.concat([dropPath])
      saveTouched(state.appId, list, map, null, effectedPaths)

      return {
        ...state,
        list,
        map,
        hoverSelection: [],
      }
    }

    if (action.type === REORDER_OBJECTS_MOVE_DOWN) {
      const { ids } = action
      let { list, map, magicLayout } = state

      const actualPath = map[ids]

      const lastLayerCheck = actualPath === '0.0'

      if (lastLayerCheck) {
        return state
      }

      let dropPath = decrementPath(actualPath)

      let itemPaths = ids.map(id => map[id]).filter(i => i)

      for (let i = 0; i < itemPaths.length; i += 1) {
        const path = itemPaths[i]

        if (isChildPath(path, dropPath)) {
          return state
        }
      }

      itemPaths = sortPaths(itemPaths)
      const items = itemPaths.map(path => getObject(list, path))

      dropPath = getDropPath(dropPath, itemPaths)

      itemPaths
        .slice()
        .reverse()
        .forEach(path => {
          list = remove(list, path)
          list = updateParentBounds(
            list,
            map,
            null,
            path,
            resizeParent,
            undefined,
            magicLayout
          )
          list = updateParentOptions(list, map, null, path)
        })

      list = insert(list, dropPath, ...items)
      map = remapSiblings(list, map, '0')

      items.forEach(itm => {
        list = updateParentBounds(
          list,
          map,
          itm.id,
          null,
          resizeParent,
          undefined,
          magicLayout
        )
        list = updateOptions(list, map, itm.id)
      })

      const effectedPaths = itemPaths.concat([dropPath])
      saveTouched(state.appId, list, map, null, effectedPaths)

      return {
        ...state,
        list,
        map,
        hoverSelection: [],
      }
    }

    if (
      action.type === GROUP_OBJECTS ||
      action.type === GROUP_OBJECTS_TO_LIST
    ) {
      let { list, map, magicLayout } = state
      const { ids } = action

      if (ids.length === 0) {
        return state
      }

      let paths = ids.map(id => map[id])
      paths = paths.filter(path => pathLength(path) > 1)
      paths = removeChildren(sortPaths(paths))

      const group = createEmptyObject(state.list, map, {
        type: action.type === GROUP_OBJECTS_TO_LIST ? LIST : GROUP,
        children: [],
        width: 0,
        height: 0,
      })

      const groupPath = getGroupPath(paths)

      if (!groupPath) {
        return
      }

      paths.forEach(path => {
        const obj = getObject(list, path)
        group.children.push(obj)
      })

      const pathsReversed = [...paths]
      pathsReversed.reverse()

      pathsReversed.forEach(path => {
        list = remove(list, path)
      })

      list = insert(list, groupPath, group)
      map = remapSiblings(list, map, groupPath)

      list = updateBounds(
        list,
        map,
        groupPath,
        resizeParent,
        undefined,
        magicLayout
      )

      if (action.type === GROUP_OBJECTS_TO_LIST) {
        let group = getObject(list, groupPath)

        group = {
          ...group,
          height: group.height * 3 + (group.rowMargin || 0) * 2,
        }

        list = update(list, groupPath, group)
      }

      const selection = [group.id]

      saveTouched(state.appId, list, map, null, paths)

      return updateIndices({
        ...state,
        list,
        map,
        selection,
        hoverSelection: [],
      })
    }

    if (action.type === UNGROUP_OBJECTS) {
      const { ids } = action
      let { list, map } = state

      const paths = ids.map(id => map[id])
      let objects = paths.map(path => getObject(list, path))

      objects = objects.filter(obj => obj.type === GROUP)

      let selection = []

      objects.forEach(group => {
        const { id, children } = group
        const path = map[id]

        selection = selection.concat(children.map(c => c.id))

        list = remove(list, path)
        list = insert(list, path, ...children)
        map = remapSiblings(list, map, '0')
      })

      saveTouched(state.appId, list, map, null, paths)

      return updateIndices({
        ...state,
        list,
        map,
        selection,
      })
    }

    if (action.type === ZOOM) {
      const { relativeScale, relativeOffset } = action
      let { scale, offset } = action

      const newState = { ...state.zoom }

      if (relativeScale) {
        scale = state.zoom.scale * relativeScale
      }

      if (relativeOffset) {
        const [diffX, diffY] = relativeOffset
        const [x, y] = state.zoom.offset

        offset = [x + diffX, y + diffY]
      }

      if (scale && !offset) {
        const xCenter = (window.innerWidth - 430) / 2 + 430
        const yCenter = (window.innerHeight - 64) / 2 + 64
        const center = [xCenter, yCenter]

        offset = getOffset(scale, center, state.zoom.scale, state.zoom.offset)
      }

      if (scale) {
        newState.scale = scale
      }

      if (offset) {
        newState.offset = offset
      }

      if (newState.scale > 64 || newState.scale < 1.0 / 16) {
        return state
      }

      return {
        ...state,
        zoomAppId: state.appId,
        zoom: newState,
      }
    }

    if (action.type === RESET_ZOOM) {
      const { list } = state
      const zoom = calculateZoom(getBoundingBox(list))

      return { ...state, zoom }
    }

    if (action.type === '@@redux-undo/UNDO') {
      saveDiffed(state.appId, state.list)
    } else if (action.type === '@@redux-undo/REDO') {
      saveDiffed(state.appId, state.list)
    }

    if (action.type === SET_TOOL) {
      return {
        ...state,
      }
    }

    if (action.type === SELECT_PARENT) {
      const { objectId } = action

      return {
        ...state,
        selectedParent: objectId,
      }
    }

    if (action.type === PAN) {
      return {
        ...state,
        panning: action.value,
      }
    }

    if (action.type === SET_LIBRARY_GLOBAL) {
      const { appId } = state
      let { libraryGlobals } = state
      const { libraryName, componentName, changes } = action

      libraryGlobals = {
        ...state.libraryGlobals,
        [libraryName]: {
          ...state.libraryGlobals[libraryName],
          [componentName]: deepMerge(
            state.libraryGlobals[libraryName]?.[componentName],
            changes
          ),
        },
      }

      saveLibraryGlobals(appId, libraryGlobals)

      return { ...state, libraryGlobals }
    }

    if (action.type === RUN_INSTRUCTIONS) {
      const { instructions, globalState } = action
      const app = getApp(globalState, state.appId)

      const instructionState = {
        list: state.list,
        pathMap: state.map,
        selection: state.selection,
        featureFlags: globalState.featureFlags,
        app,
      }

      const { list, pathMap, selection } = applyInstructions(
        instructionState,
        instructions
      )

      // TODO (Tom) Skip Save when?
      const touched = [...selection, ...getTouchedFromInstruction(instructions)]
      if (touched.length > 0) {
        saveTouched(state.appId, list, pathMap, touched)
      }

      return updateIndices({
        ...state,
        list,
        map: pathMap,
        selection,
        hoverSelection: [],
      })
    }

    if (action.type === RUN_SELECTION_INSTRUCTION) {
      // one instruction at a time
      const { instruction, globalState } = action

      // prevent running instructions on section elements
      if (Array.isArray(instruction.options?.objectIds)) {
        for (const objectId of instruction.options.objectIds) {
          const object = getObject(state.list, state.map[objectId])

          if (isSectionElement(object)) {
            return state
          }
        }
      }

      // the only thing that will change is the list,
      // since we're not changing the object hierarchy or selection
      const { list } = applyLayoutInstruction(
        {
          list: state.list,
          pathMap: state.map,
          selection: state.selection,
          featureFlags: globalState.featureFlags,
        },
        instruction
      )

      const touched = getSelectionTouchedFromInstruction(instruction)
      if (touched.length > 0) {
        saveTouched(state.appId, list, state.map, touched)
      }

      return {
        ...state,
        list,
      }
    }

    return state
  }
)

// ACTIONS

export const requestData = appId => ({
  type: REQUEST_DATA,
  appId,
})

export const setData = (appId, data, app) => dispatch => {
  dispatch({
    type: SET_DATA,
    appId,
    data,
    app,
  })

  window.setTimeout(() => {
    dispatch({ type: '@@redux-undo/CLEAR_HISTORY' })
  }, 0)
}

export const createObject =
  (object, path = null, silent = false, mouseCoords = {}, parentId) =>
  (dispatch, getState) =>
    dispatch({
      type: CREATE_OBJECT,
      object,
      path,
      silent,
      id: getId(),
      mouseCoords,
      globalState: getState(),
      parentId,
    })

export const updateObject =
  (id, object, undoGroupKey) => (dispatch, getState) =>
    dispatch({
      type: UPDATE_OBJECT,
      id,
      object,
      undoGroup: undoGroupKey && `${undoGroupKey}-${id}`,
      globalState: getState(),
    })

export const updateObjects = objects => (dispatch, getState) =>
  dispatch({
    type: UPDATE_OBJECTS,
    objects,
    globalState: getState(),
  })

export const updateObjectBounds = id => (dispatch, getState) =>
  dispatch({
    type: UPDATE_OBJECT_BOUNDS,
    id,
    globalState: getState(),
  })

// TODO (michael-adalo): appears to be unused?
export const changeObjectType = (id, newType) => ({
  type: CHANGE_OBJECT_TYPE,
  id,
  newType,
})

export const resizeObject =
  (id, object, skipSave = false, resetAccordion = false) =>
  (dispatch, getState) =>
    dispatch({
      type: RESIZE_OBJECT,
      id,
      object,
      skipSave,
      globalState: getState(),
      resetAccordion,
    })

export const positionObjects =
  (ids, offset, shiftKey, shouldSave = true) =>
  (dispatch, getState) =>
    dispatch({
      type: POSITION_OBJECTS,
      ids,
      offset,
      shiftKey,
      shouldSave,
      globalState: getState(),
    })

export const alignObjects = direction => ({
  type: ALIGN_OBJECTS,
  direction,
})

export const deleteObject = id => ({
  type: DELETE_OBJECT,
  id,
})

export const reorderObjects = (ids, dropTarget, options) => ({
  type: REORDER_OBJECTS,
  ids,
  dropTarget,
  options,
})

export const reorderObjectsMoveFirst = ids => ({
  type: REORDER_OBJECTS_MOVE_FIRST,
  ids,
})

export const reorderObjectsMoveLast = ids => ({
  type: REORDER_OBJECTS_MOVE_LAST,
  ids,
})

export const reorderObjectsMoveUp = ids => ({
  type: REORDER_OBJECTS_MOVE_UP,
  ids,
})

export const reorderObjectsMoveDown = ids => ({
  type: REORDER_OBJECTS_MOVE_DOWN,
  ids,
})

export const groupObjectsToList = ids => ({
  type: GROUP_OBJECTS_TO_LIST,
  ids,
})

export const groupObjects = ids => ({
  type: GROUP_OBJECTS,
  ids,
})

export const ungroupObjects = ids => ({
  type: UNGROUP_OBJECTS,
  ids,
})

export const setZoom = (scale, offset, relativeScale, relativeOffset) => ({
  type: ZOOM,
  scale,
  offset,
  relativeScale,
  relativeOffset,
})

export const resetZoom = () => ({ type: RESET_ZOOM })

export const selectParent = id => ({
  type: SELECT_PARENT,
  objectId: id,
})

export const setPan = value => (dispatch, getState) => {
  const state = getState()
  const panning = getPanning(state)

  if (value === true && panning === true) return false

  return dispatch({
    type: PAN,
    value,
  })
}

export const setLibraryGlobals = (libraryName, componentName, changes) => ({
  type: SET_LIBRARY_GLOBAL,
  libraryName,
  componentName,
  changes,
})

export const runInstructions = instructions => (dispatch, getState) =>
  dispatch({
    type: RUN_INSTRUCTIONS,
    instructions,
    globalState: getState(),
  })

export const runSelectionInstruction = instruction => (dispatch, getState) =>
  dispatch({
    type: RUN_SELECTION_INSTRUCTION,
    instruction,
    globalState: getState(),
  })

// SELECTORS

export const selectObjects = (state, ids = null) => {
  const list = state.editor.objects.present.list

  if (!ids) {
    return list
  }

  ids = uniqueElements(ids)

  return ids.map(id => getObject(list, getPath(state, id))).filter(o => o)
}

// Used by marquee selection
export const selectVisible = state => {
  return state.editor.objects.present.list.filter(o => !o.hidden)
}

export const selectObject = (state, id) => {
  const idComponents = id && typeof id === 'string' ? id.split('.') : undefined

  id = idComponents && idComponents.length > 0 ? idComponents[0] : id
  const path = getPath(state, id)
  const objects = selectObjects(state)

  return getObject(objects, path)
}

export const getObjectList = state => {
  return state.editor.objects.present.list
}

export const getMap = state => {
  return state.editor.objects.present.map
}

export const getPath = (state, objectId) => {
  return state.editor.objects.present.map[objectId]
}

const getSelectedSub = (objects, selection) => {
  const { map, list } = objects

  const result = selection.map(id => getObject(list, map[id]))

  const idMap = {}

  return result.filter(obj => {
    if (!obj) {
      return false
    }

    const { id } = obj

    if (idMap[id]) {
      return false
    }

    idMap[id] = true

    return true
  })
}

export const getSelectedObjects = state => {
  const { objects } = state.editor
  const selection = objects.present.selection

  return getSelectedSub(objects.present, selection)
}

export const getSelectedIds = state => {
  return state.editor.objects.present.selection
}

export const getZoom = ({ editor }) => editor.objects.present.zoom

export const getPage = state => {
  const { width, height, backgroundColor } = state.editor.objects.present

  return { width, height, backgroundColor }
}

export const getCurrentAppId = state => {
  const { appId } = state?.editor?.objects?.present || {}

  return appId
}

/**
 * Indicates if the current app is responsive.
 *
 * @param {any} state The current redux state
 * @returns {boolean} True if the current app is responsive, otherwise false.
 */
export const getMagicLayout = state => {
  const appId = getCurrentAppId(state)

  return state?.apps?.apps[appId]?.magicLayout || false
}

export const getObjectsOfType = (state, componentId, type) => {
  const { typeIndex } = state.editor.objects.present
  let index = (typeIndex[componentId] || {})[type]

  if (!componentId) {
    index = []

    for (const component of state.editor.objects.present.list) {
      index = index.concat((typeIndex[component.id] || {})[type] || [])
    }
  }

  return index || []
}

export const getLibraryInputs = (state, componentId, allowedDataTypes) => {
  const libraryObjects = getObjectsOfType(state, componentId, LIBRARY_COMPONENT)
  const app = state.apps.apps[getCurrentAppId(state)]

  // Results will have the form: { objectId, fieldId: [childComponent, prop] }
  const results = []

  const defaultDataType = dataTypes.TEXT

  for (const id of libraryObjects) {
    const obj = selectObject(state, id)

    if (!obj) continue

    const { libraryName, componentName } = obj
    const config = getAppComponent(app, libraryName, componentName)

    if (!config) continue

    // Loop through props
    for (const prop of config.props) {
      const dataType = prop.type ?? defaultDataType
      if (
        hasRole(prop, 'formValue') &&
        (!allowedDataTypes || allowedDataTypes.includes(dataType))
      ) {
        results.push({
          objectId: id,
          prop: [prop.name],
          dataType,
        })
      }
    }

    // Loop through child components
    for (const child of config.childComponents || []) {
      for (const prop of child.props) {
        const dataType = prop.type ?? defaultDataType
        if (
          hasRole(prop, 'formValue') &&
          (!allowedDataTypes || allowedDataTypes.includes(dataType))
        ) {
          results.push({
            objectId: id,
            prop: [child.name, prop.name],
            dataType,
          })
        }
      }
    }
  }

  return results
}

export const getInputs = (state, componentId, objectId, objectType = INPUT) => {
  const inputs = getLibraryInputs(state, componentId, [
    dataTypes.TEXT,
    dataTypes.NUMBER,
  ])
  inputs.push(...getObjectsOfType(state, componentId, objectType))

  const list = state.editor.objects.present?.list

  const inputIds = inputs.map(id => {
    const path = getPath(state, id)

    const listParentIds = []

    for (let i = pathLength(path) - 1; i > 0; i -= 1) {
      const obj = getObject(list, subPath(path, pathLength(path) - i))

      if (!obj) continue
      if (obj.type === LIST) listParentIds.push(obj.id)
    }

    if (listParentIds.length === 0) return id

    return listParentIds.concat([id])
  })

  const objectListParents = []
  const path = getPath(state, objectId)

  for (let i = pathLength(path) - 1; i > 0; i -= 1) {
    const obj = getObject(list, subPath(path, pathLength(path) - i))

    if (!obj) continue
    if (obj.type === LIST) objectListParents.push(obj.id)
  }

  return inputIds.filter(id => {
    if (!Array.isArray(id)) return id
    const inputPath = id.slice(0, id.length - 1).join('.')
    const objectPath = objectListParents.join('.')

    return objectPath.startsWith(inputPath)
  })
}

export const getLocationInputs = (state, componentId) => [
  ...getLibraryInputs(state, componentId, [dataTypes.LOCATION]),
  ...getObjectsOfType(state, componentId, LOCATION_INPUT),
]

export const getImagePickers = (state, componentId) => {
  const inputs = getLibraryInputs(state, componentId, [dataTypes.IMAGE])

  inputs.push(...getObjectsOfType(state, componentId, IMAGE_UPLOAD))

  return inputs
}

export const getFilePickers = (state, componentId) => {
  const inputs = getLibraryInputs(state, componentId, [dataTypes.FILE])
  inputs.push(...getObjectsOfType(state, componentId, FILE_UPLOAD))

  return inputs
}

export const getDatePickers = (state, componentId) => {
  const inputs = getLibraryInputs(state, componentId, [
    dataTypes.DATE,
    dataTypes.DATE_ONLY,
  ])

  inputs.push(...getObjectsOfType(state, componentId, DATE_PICKER))

  return inputs
}

export const getSelects = (state, componentId, objectId) => {
  return getInputs(state, componentId, objectId, SELECT)
}

export const getLists = (state, componentId) => {
  return getObjectsOfType(state, componentId, LIST)
}

export const getCheckboxes = (state, componentId) => {
  return getLibraryInputs(state, componentId, [dataTypes.BOOLEAN])
}

export const getLoading = state => state.editor.objects.present.loading

export const getName = state => state.editor.objects.present.name

export const getComponent = (state, objectId) => {
  const path = getPath(state, objectId)

  if (!path) {
    return null
  }

  const componentPath = subPath(path, 1)
  const objects = selectObjects(state)
  const component = getObject(objects, componentPath)

  return component || null
}

export const getComponentId = (state, objectId) => {
  const component = getComponent(state, objectId)

  return (component && component.id) || null
}

export const getObjectPosition = (state, objectId) => {
  const object = selectObject(state, objectId)

  if (!object || object.type === COMPONENT) {
    return object
  }

  const component = getComponent(state, objectId)
  const deviceType = getDeviceType(component.width)
  const positionedObject = getDeviceObject(object, deviceType)
  const { x, y } = positionedObject

  const { width, height } = positionedObject

  return {
    width,
    height,
    x: x + component.x,
    y: y + component.y,
  }
}

export const getDataBinding = (state, objectId) => {
  const object = selectObject(state, objectId)

  return object && object.dataBindings && object.dataBindings[0]
}

export const getActions = (state, objectId) => {
  const object = selectObject(state, objectId)

  return (object && object.actions) || {}
}

export const getParentComponent = (state, objectId) => {
  const { list, map } = state.editor.objects.present

  if (objectId && map[objectId]) {
    const parentPath = getParentPath(map[objectId])

    return getObject(list, parentPath)
  }

  return null
}

export const getParentScreenComponent = (state, objectId) => {
  const list = getObjectList(state)
  const map = getMap(state)

  if (!map[objectId]) {
    throw new Error(`Object not found: ${objectId}`)
  }

  if (objectId && map[objectId]) {
    const parentScreen = getParentScreen(list, map, objectId)

    if (!parentScreen) {
      throw new Error(`Failed to resolve parent screen for ${objectId}`)
    }

    return parentScreen
  }

  return null
}

export const getPlatform = state => {
  const appId = getCurrentAppId(state)
  const app = state?.apps?.apps?.[appId]

  return app?.primaryPlatform || 'mobile'
}

export const getYOffset = state => {
  const platform = getPlatform(state)

  if (isSupportedOnWeb(platform)) {
    return 20
  }

  return 0
}

export const getSelectedParent = state => {
  const { selectedParent } = state.editor.objects.present

  if (!selectedParent) {
    return null
  }

  const objects = selectObjects(state, [selectedParent])

  return objects ? objects[0] : null
}

export const getPanning = state => {
  return state.editor.objects.present.panning
}

export const getLibraryGlobals = (state, libraryName, componentName) => {
  const globals = state.editor.objects.present.libraryGlobals

  return globals[libraryName]?.[componentName] || {}
}

const isParentVisible = (object, device, list, map) => {
  const parentPath = getParentPath(map[object.id])
  const parent = getObject(list, parentPath)

  if (!parent || parent.type === COMPONENT) {
    return true
  }

  if (parent.deviceVisibility && !parent.deviceVisibility[device]) {
    return false
  }

  return isParentVisible(parent, device, list, map)
}
